import { useState, useCallback, useEffect, useRef, useMemo, useLayoutEffect } from 'react';

// Debug flag - set to true to see focus mode state changes in console
const DEBUG_FOCUS = false;
const debugLog = (...args) => DEBUG_FOCUS && console.log('[FocusState]', ...args);

/**
 * useFocusState Hook
 * 
 * Manages focus mode state:
 * - Which tasks are focused
 * - Cleanup when tasks are deleted
 * - Focus mode change tracking
 */
export function useFocusState({
  tasks,
  onFocusModeChange,
  initialFocusedTaskIds = null, // Initial state for persistence across tab switches
}) {
  // Internal focus mode state - supports multiple task IDs
  const [internalFocusedTaskIds, setInternalFocusedTaskIds] = useState(() => {
    if (initialFocusedTaskIds && initialFocusedTaskIds.size > 0) {
      return initialFocusedTaskIds;
    }
    return null;
  });
  
  // Track the previous initialFocusedTaskIds signature to detect changes
  const prevInitialSignatureRef = useRef(
    initialFocusedTaskIds ? Array.from(initialFocusedTaskIds).sort().join(',') : null
  );
  
  // Compute current signature
  const currentInitialSignature = initialFocusedTaskIds 
    ? Array.from(initialFocusedTaskIds).sort().join(',') 
    : null;
  
  // Detect if initialFocusedTaskIds changed THIS render
  // This allows us to use the new value immediately without waiting for an effect
  const initialChanged = currentInitialSignature !== prevInitialSignatureRef.current;
  
  // Determine effective focusedTaskIds:
  // - If initialFocusedTaskIds just changed, use it immediately (prevents flash)
  // - Otherwise use internal state
  let focusedTaskIds = internalFocusedTaskIds;
  if (initialChanged) {
    focusedTaskIds = initialFocusedTaskIds;
    debugLog('Using initialFocusedTaskIds immediately (changed this render)', { currentInitialSignature });
  }
  
  // Sync internal state with initialFocusedTaskIds when it changes
  // This runs after render to update internal state for future renders
  useLayoutEffect(() => {
    if (initialChanged) {
      prevInitialSignatureRef.current = currentInitialSignature;
      if (initialFocusedTaskIds && initialFocusedTaskIds.size > 0) {
        setInternalFocusedTaskIds(initialFocusedTaskIds);
      } else {
        setInternalFocusedTaskIds(null);
      }
    }
  }, [initialChanged, currentInitialSignature, initialFocusedTaskIds]);
  
  // Wrapper for setFocusedTaskIds that updates internal state
  const setFocusedTaskIds = useCallback((newValue) => {
    setInternalFocusedTaskIds(newValue);
  }, []);
  
  // Track if we're in "filter focus mode" - intentionally showing empty results
  // This prevents cleanup from exiting focus mode when there are no matches
  const isFilterFocusModeRef = useRef(false);
  
  // Track previous focus state to detect changes
  const prevFocusedTaskIdsRef = useRef(null);
  const hasResetForFocusRef = useRef(false);
  
  // Keep track of the last focused IDs for exit animation
  const lastFocusedIdsRef = useRef(null);
  if (focusedTaskIds && focusedTaskIds.size > 0) {
    lastFocusedIdsRef.current = focusedTaskIds;
  }
  
  // Track "pending" task IDs that were just added and may not exist in tasks yet
  // These should NOT be cleaned up by the effect below
  const pendingTaskIdsRef = useRef(new Set());
  
  // Clean up focusedTaskIds when tasks are deleted
  const taskIds = useMemo(() => new Set(tasks.map(t => t.id)), [tasks]);
  
  useEffect(() => {
    // Don't cleanup if we're in filter focus mode (intentionally empty)
    if (isFilterFocusModeRef.current) return;
    if (!focusedTaskIds || focusedTaskIds.size === 0) return;
    
    const validIds = new Set();
    let hasInvalidIds = false;
    const invalidIds = [];
    
    focusedTaskIds.forEach(id => {
      if (taskIds.has(id)) {
        validIds.add(id);
        // If this was a pending ID that now exists, remove from pending
        pendingTaskIdsRef.current.delete(id);
      } else if (pendingTaskIdsRef.current.has(id)) {
        // This ID is pending (just added, task not created yet) - keep it
        validIds.add(id);
      } else {
        hasInvalidIds = true;
        invalidIds.push(id);
      }
    });
    
    if (hasInvalidIds) {
      debugLog('Cleanup: removing invalid IDs', { invalidIds, validIds: Array.from(validIds), pending: Array.from(pendingTaskIdsRef.current) });
      if (validIds.size === 0) {
        debugLog('Cleanup: no valid IDs left, exiting focus mode');
        setFocusedTaskIds(null);
        onFocusModeChange?.(false, null);
      } else {
        setFocusedTaskIds(validIds);
      }
    }
  }, [taskIds, focusedTaskIds, onFocusModeChange]);
  
  // Handle focus on a single task
  const handleFocusTask = useCallback((taskId) => {
    debugLog('handleFocusTask called', { taskId, currentFocused: focusedTaskIds ? Array.from(focusedTaskIds) : null });
    const newSet = new Set([taskId]);
    setFocusedTaskIds(newSet);
    onFocusModeChange?.(true, taskId, false, newSet);
  }, [onFocusModeChange, focusedTaskIds]);
  
  // Handle focus on multiple tasks (can be empty array for "no matches" state)
  // isFilterMode: true when called from filter dropdown (allows empty results)
  const handleFocusTasks = useCallback((taskIds, isFilterMode = false) => {
    debugLog('handleFocusTasks called', { taskIds, isFilterMode, currentFocused: focusedTaskIds ? Array.from(focusedTaskIds) : null });
    if (!taskIds) {
      debugLog('handleFocusTasks: null taskIds, returning');
      return false;
    }
    
    // Track if we're in filter focus mode (allows empty results without cleanup exiting)
    isFilterFocusModeRef.current = isFilterMode;
    
    const newSet = new Set(taskIds);
    
    const isTransition = focusedTaskIds !== null;
    debugLog('handleFocusTasks: setting new focus', { newFocused: Array.from(newSet), isTransition, isFilterMode });
    
    setFocusedTaskIds(newSet);
    onFocusModeChange?.(true, taskIds[0] || null, isTransition, newSet);
    
    return isTransition;
  }, [focusedTaskIds, onFocusModeChange]);
  
  // Handle exit focus mode
  const handleExitFocusMode = useCallback((instant = false) => {
    debugLog('handleExitFocusMode called', { currentFocused: focusedTaskIds ? Array.from(focusedTaskIds) : null, instant });
    isFilterFocusModeRef.current = false;
    setFocusedTaskIds(null);
    onFocusModeChange?.(false, null, false, null, instant);
  }, [onFocusModeChange, focusedTaskIds]);
  
  // Handle instant focus on multiple tasks (no animation - for view switching)
  const handleFocusTasksInstant = useCallback((taskIds) => {
    debugLog('handleFocusTasksInstant called', { taskIds });
    if (!taskIds) return;
    
    const newSet = new Set(taskIds);
    setFocusedTaskIds(newSet);
    // Pass instant=true as 5th parameter
    onFocusModeChange?.(true, taskIds[0] || null, false, newSet, true);
  }, [onFocusModeChange]);
  
  // Handle instant exit (no animation - for view switching)
  const handleExitFocusModeInstant = useCallback(() => {
    debugLog('handleExitFocusModeInstant called');
    isFilterFocusModeRef.current = false;
    setFocusedTaskIds(null);
    onFocusModeChange?.(false, null, false, null, true);
  }, [onFocusModeChange]);
  
  // Add task(s) to the focused set (for expanding focus when adding connected nodes)
  // If parentTaskId is provided and not in focusedTaskIds, it will be promoted to focused
  const addToFocusedTasks = useCallback((newTaskIds, parentTaskId = null) => {
    debugLog('addToFocusedTasks called', { 
      newTaskIds, 
      parentTaskId, 
      currentFocused: focusedTaskIds ? Array.from(focusedTaskIds) : null,
      isFocusMode: focusedTaskIds !== null && focusedTaskIds.size > 0
    });
    
    if (!focusedTaskIds || focusedTaskIds.size === 0) {
      debugLog('addToFocusedTasks: not in focus mode, ignoring');
      return;
    }
    
    // Mark new IDs as pending so they don't get cleaned up before task is created
    const idsToAdd = Array.isArray(newTaskIds) ? newTaskIds : [newTaskIds];
    idsToAdd.forEach(id => pendingTaskIdsRef.current.add(id));
    debugLog('addToFocusedTasks: added to pending', { pending: Array.from(pendingTaskIdsRef.current) });
    
    setFocusedTaskIds(prev => {
      const newSet = new Set(prev);
      
      // If parent is being promoted from faded to focused, add it
      if (parentTaskId && !newSet.has(parentTaskId)) {
        debugLog('addToFocusedTasks: promoting parent to focused', { parentTaskId });
        newSet.add(parentTaskId);
      }
      
      // Add the new task(s)
      idsToAdd.forEach(id => newSet.add(id));
      
      debugLog('addToFocusedTasks: new focused set', { newFocused: Array.from(newSet) });
      return newSet;
    });
  }, [focusedTaskIds]);
  
  // Create signature for change tracking
  const focusedTaskIdsSignature = useMemo(() => {
    if (!focusedTaskIds) return null;
    return Array.from(focusedTaskIds).sort().join(',');
  }, [focusedTaskIds]);
  
  // Track focus mode signature changes
  useEffect(() => {
    if (focusedTaskIdsSignature && 
        focusedTaskIdsSignature !== prevFocusedTaskIdsRef.current && 
        !hasResetForFocusRef.current) {
      hasResetForFocusRef.current = true;
      prevFocusedTaskIdsRef.current = focusedTaskIdsSignature;
    }
    
    if (!focusedTaskIdsSignature) {
      hasResetForFocusRef.current = false;
      prevFocusedTaskIdsRef.current = null;
    }
  }, [focusedTaskIdsSignature]);
  
  return {
    focusedTaskIds,
    setFocusedTaskIds,
    lastFocusedTaskIds: lastFocusedIdsRef.current,
    focusedTaskIdsSignature,
    isFocusMode: focusedTaskIds !== null, // Can be in focus mode with empty set (no matches)
    
    // Handlers
    handleFocusTask,
    handleFocusTasks,
    handleExitFocusMode,
    addToFocusedTasks,
    
    // Instant handlers (no animation - for view switching)
    handleFocusTasksInstant,
    handleExitFocusModeInstant,
  };
}

